import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import EventRow from '@/pages/events/components/EventRow';
import { events, categories } from '@/mocks/events';
import type { HotEvent } from '@/mocks/events';
import { setPageMeta, injectJsonLd, clearJsonLd, buildBreadcrumbLd, SITE_BASE } from '@/utils/seo';

const statusFilter = ['全部', '发酵中', '持续更新', '已告一段落'] as const;

function catCount(cat: string) {
  if (cat === '全部') return events.length;
  return events.filter((e) => e.category === cat).length;
}

export default function Events() {
  const [params, setParams] = useSearchParams();
  const cat = params.get('cat') || '全部';
  const status = params.get('status') || '全部';
  const [keyword, setKeyword] = useState(params.get('q') || '');
  const [query, setQuery] = useState(keyword);

  useEffect(() => {
    setPageMeta({
      title: '吃瓜事件时间轴列表 - 瓜迹热点事件经过与进展',
      description:
        '浏览瓜迹全部吃瓜事件时间轴：按直播电商、综艺、品牌、票务、探店等栏目分类，每起热点事件的经过与进展、爆发到后续追踪的节点都清晰可查，持续更新。',
      keywords: '吃瓜事件时间轴,吃瓜事件经过,吃瓜事件进展,热点事件节点,吃瓜事件列表',
      path: '/events',
    });
    const c1 = injectJsonLd(buildBreadcrumbLd([{ name: '首页', path: '/' }, { name: '事件时间轴', path: '/events' }]));
    const c2 = injectJsonLd({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '吃瓜事件时间轴列表',
      url: `${SITE_BASE}/events`,
      mainEntity: { '@type': 'ItemList', itemListElement: events.map((e, i) => ({ '@type': 'ListItem', position: i + 1, name: e.title, url: `${SITE_BASE}/event/${e.id}` })) },
    });
    return () => {
      clearJsonLd();
      c1();
      c2();
    };
  }, []);

  const filtered = useMemo(() => {
    const k = query.trim().toLowerCase();
    let list: HotEvent[] = [...events];
    if (cat !== '全部') list = list.filter((e) => e.category === cat);
    if (status !== '全部') list = list.filter((e) => e.status === status);
    if (k) {
      list = list.filter(
        (e) =>
          e.title.toLowerCase().includes(k) ||
          e.summary.toLowerCase().includes(k) ||
          e.tags.some((t) => t.toLowerCase().includes(k)) ||
          e.category.includes(query.trim()),
      );
    }
    return list.sort((a, b) => b.heat - a.heat || new Date(b.updated).getTime() - new Date(a.updated).getTime());
  }, [cat, status, query]);

  const liveCount = events.filter((e) => e.status === '发酵中').length;

  const update = (key: string, value: string, clearQuery = false) => {
    const next = new URLSearchParams(params);
    if (value === '全部' || value === '') next.delete(key);
    else next.set(key, value);
    if (clearQuery) next.delete('q');
    setParams(next);
  };

  return (
    <Layout>
      {/* 页头 */}
      <section className="border-b border-background-300/60 bg-background-100/60">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">吃瓜事件时间轴</span>
          </nav>
          <div className="mt-4 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
            <div>
              <h1 className="font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">吃瓜事件时间轴</h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                每起热点事件按节点梳理经过与进展，从爆发到后续追踪一目了然。共收录 <b className="text-primary-600 tabular-nums">{events.length}</b> 起事件，其中{' '}
                <b className="text-accent-700 tabular-nums">{liveCount}</b> 起仍在发酵。
              </p>
            </div>
            <label className="flex items-center gap-2 rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 focus-within:border-primary-400 lg:w-72">
              <i className="ri-search-line text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setQuery(keyword);
                    update('q', keyword, true);
                  }
                }}
                placeholder="搜索事件 / 关键词…"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button type="button" onClick={() => { setQuery(keyword); update('q', keyword, true); }} className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50 hover:bg-primary-600">
                搜索
              </button>
            </label>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <div className="grid gap-8 lg:grid-cols-4">
          {/* 左侧筛选 */}
          <aside className="lg:sticky lg:top-32 lg:self-start">
            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                <i className="ri-price-tag-3-line text-primary-500" /> 事件栏目
              </h3>
              <div className="mt-3 flex flex-wrap gap-2 lg:flex-col lg:gap-1">
                {categories.map((c) => {
                  const active = cat === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => update('cat', c, true)}
                      className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${
                        active ? 'bg-primary-50 font-medium text-primary-700' : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <span className="flex items-center gap-2">{active && <i className="ri-checkbox-circle-fill text-primary-500" />}{c}</span>
                      <span className="text-xs text-foreground-400 tabular-nums">{catCount(c)}</span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 border-t border-background-200 pt-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                  <i className="ri-live-line text-accent-500" /> 追踪状态
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {statusFilter.map((s) => {
                    const active = status === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => update('status', s)}
                        className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                          active ? 'border-accent-300 bg-accent-100 text-accent-900' : 'border-background-200 bg-background-50 text-foreground-600 hover:border-accent-200 hover:text-accent-800'
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 rounded-lg bg-secondary-100/70 p-3.5">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-secondary-800"><i className="ri-question-line" /> 没找到想追更的事件？</p>
                <p className="mt-1.5 text-xs leading-relaxed text-secondary-700">欢迎提供公开可查的线索，编辑核实后会补全时间轴。</p>
                <a href="mailto:hello@guaji.team" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-secondary-900 hover:text-primary-700">
                  联系瓜迹 <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </aside>

          {/* 右侧列表 */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-foreground-500">
                {cat !== '全部' || status !== '全部' || query ? `找到 ` : ''}
                <b className="text-foreground-900 tabular-nums">{filtered.length}</b> 起事件
                {cat !== '全部' && <span> · {cat}</span>}
                {status !== '全部' && <span> · {status}</span>}
                {query && <span> · “{query}”</span>}
              </p>
              {(cat !== '全部' || status !== '全部' || query) && (
                <button type="button" onClick={() => { setQuery(''); setKeyword(''); setParams({}); }} className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700">
                  <i className="ri-restart-line" /> 清除筛选
                </button>
              )}
            </div>

            {filtered.length === 0 ? (
              <div className="mt-4 rounded-xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <i className="ri-inbox-2-line text-4xl text-foreground-300" />
                <p className="mt-3 text-sm text-foreground-500">当前筛选条件下暂无事件，换个栏目或状态试试。</p>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                {filtered.map((ev) => (
                  <EventRow key={ev.id} event={ev} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}